import React, { useCallback, useEffect, useState } from 'react'

import {
  useGetNodeAtPosition,
  useLockCamera,
  useMapRelativePositionToCameraPosition,
  usePRFileSections,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'

import { DraftCommentProvider } from '@sketch/modules-common'
import { AnnotationDotFragment } from '@sketch/gql-types'

import { AnnotationOverlayContext } from '../../../annotations/components'
import { MouseCoordinates } from '../../../annotations/types'
import {
  useHideAnnotationDots,
  useVisibleEntityUUIDs,
} from '../../../annotations/hooks'
import {
  getAnnotatableNode,
  getAnnotationAnchorUUID,
  isAnnotationOnPage,
} from '../../../annotations/utils'
import { useCanvasScope } from '../../scope'
import { useGetActiveSidebarTab } from '../../../shares/components/ShareSidebarTabContext'
import { INSPECTOR_SEGMENT } from '../../../shares/constants'

type CanvasAnnotationOverlayContextProps = Pick<
  React.ComponentProps<typeof AnnotationOverlayContext>,
  'disabled' | 'children'
> & {
  isViewingLatestVersion: boolean
}

/**
 * Where an annotation lands when placed on the canvas, and which of the
 * existing ones are drawn.
 *
 * Both answers follow from what the canvas is showing, so both come from the
 * scope: the page view anchors to the page and shows everything on it, while a
 * focus view anchors to and shows only what the focused entity holds. A Share
 * and a Preview differ in none of that - they had a copy of this each, which
 * differed by the single branch that stops a focused frame from taking a
 * page-level anchor.
 */
export const CanvasAnnotationOverlayContext = (
  props: CanvasAnnotationOverlayContextProps
) => {
  const scope = useCanvasScope()

  // The dots are hidden while the inspector is open, in every view. Read here
  // rather than passed, so the four route components no longer carry a flag
  // they only forward.
  const isInspectPanelOpen = useGetActiveSidebarTab() === INSPECTOR_SEGMENT
  const pageUUID = scope?.pageUUID ?? ''
  const focus = scope?.focus
  const isFocusView = !!focus && focus.kind !== 'page'
  const isFocusedFrame = focus?.kind === 'frame'

  const fileSections = usePRFileSections()

  const visibleEntityUUIDs = useVisibleEntityUUIDs({
    frameUUID: focus?.kind === 'frame' ? focus.uuid : undefined,
    sectionUUID: focus?.kind === 'section' ? focus.uuid : undefined,
  })

  const status = useStatus()
  const lockCamera = useLockCamera()
  const [lockCameraState, setLockCameraState] = useState(false)

  useEffect(() => {
    lockCamera(lockCameraState)
  }, [lockCamera, lockCameraState])

  const getNodeAtPosition = useGetNodeAtPosition()
  const mapRelativePosition = useMapRelativePositionToCameraPosition()

  // Bounds come from the PRFile, the same source the annotation dots are
  // positioned against, so a Section anchor lands where it was placed.
  const focusedSectionUUID = focus?.kind === 'section' ? focus.uuid : undefined
  const focusedSection = (fileSections ?? []).find(
    section => section.objectId === focusedSectionUUID
  )

  const getElementAtPosition = useCallback(
    async (mouseCoordinates: MouseCoordinates) => {
      const node = await getNodeAtPosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
      const annotatable = getAnnotatableNode(node)

      if (annotatable?.type === 'ARTBOARD') {
        return {
          type: 'ARTBOARD' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId: pageUUID,
        }
      }

      // In a frame focus view there is no page-level fallback - annotations can
      // only be placed on the frame itself.
      if (isFocusedFrame) {
        return null
      }

      if (annotatable?.type === 'SECTION') {
        return {
          type: 'SECTION' as const,
          permanentId: annotatable.node.getLayerUUID(),
          bounds: annotatable.node.getAbsoluteBounds(),
          permanentPageId: pageUUID,
        }
      }

      // In the single-Section focus view the page isn't on the canvas, so anchor
      // to the focused Section instead - a page annotation placed here would be
      // filtered straight back out of the view it was created in.
      if (focusedSection) {
        return {
          type: 'SECTION' as const,
          permanentId: focusedSection.objectId,
          bounds: focusedSection.bounds,
          permanentPageId: pageUUID,
        }
      }

      return {
        type: 'PAGE' as const,
        permanentId: pageUUID,
      }
    },
    [getNodeAtPosition, pageUUID, isFocusedFrame, focusedSection]
  )

  const getPositionRelativeToOrigin = useCallback(
    (mouseCoordinates: MouseCoordinates) => {
      if (status.type !== 'READY') {
        return null
      }

      return mapRelativePosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
    },
    [mapRelativePosition, status]
  )

  const shouldRenderAnnotation = useCallback(
    (annotationDot: AnnotationDotFragment) => {
      const anchorUUID = getAnnotationAnchorUUID(annotationDot)

      // Pinned to an entity: render it when that entity is on the canvas. One
      // check covers what used to be three - a focus view shows only what the
      // focused entity holds, another page's entities are not in this page's
      // file, and an annotation whose frame/Section was deleted no longer
      // resolves to anything in the scene.
      if (visibleEntityUUIDs && anchorUUID) {
        return visibleEntityUUIDs.has(anchorUUID)
      }

      // Pinned to the page itself - or the file isn't parsed yet, in which case
      // the page is all we can match on. Either way a focus view doesn't show
      // it: the page is not what's on the canvas there.
      return !isFocusView && isAnnotationOnPage(annotationDot, pageUUID)
    },
    [pageUUID, isFocusView, visibleEntityUUIDs]
  )

  const [hideAnnotationsDots] = useHideAnnotationDots()

  return (
    <DraftCommentProvider>
      <AnnotationOverlayContext
        onMovingAnnotation={setLockCameraState}
        getPositionRelativeToOrigin={getPositionRelativeToOrigin}
        getElementAtPosition={getElementAtPosition}
        hidden={hideAnnotationsDots || isInspectPanelOpen}
        shouldRenderAnnotation={shouldRenderAnnotation}
        {...props}
      />
    </DraftCommentProvider>
  )
}
